import { create } from 'zustand'
import {
  Title,
  CreateTitleData,
  UpdateTitleData,
  CreateTitleResult,
  TitleDuplicateWarning,
  listTitles,
  createTitle as apiCreate,
  updateTitle as apiUpdate,
  deleteTitle as apiDelete,
  setTitleExternalId,
  removeTitleExternalId,
  getErrorMessage,
} from '../api/titlesApi'

interface TitlesState {
  titles: Title[]
  total: number
  isLoading: boolean
  error: string | null
  duplicateWarning: TitleDuplicateWarning | null
  lastFetchParams: Record<string, any>

  fetchTitles: (params?: Record<string, any>) => Promise<void>
  createTitle: (data: CreateTitleData) => Promise<CreateTitleResult | null>
  updateTitle: (id: string, data: UpdateTitleData) => Promise<boolean>
  deleteTitle: (id: string) => Promise<boolean>
  clearError: () => void
  clearDuplicateWarning: () => void
}

export const useTitlesStore = create<TitlesState>()((set, get) => ({
  titles: [],
  total: 0,
  isLoading: false,
  error: null,
  duplicateWarning: null,
  lastFetchParams: {},

  fetchTitles: async (params = {}) => {
    set({ isLoading: true, error: null, lastFetchParams: params })
    try {
      const response = await listTitles({ ...params, limit: 100 })
      set({ titles: response.titles, total: response.total, isLoading: false })
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false })
    }
  },

  createTitle: async (data: CreateTitleData) => {
    set({ isLoading: true, error: null, duplicateWarning: null })
    try {
      const result = await apiCreate(data)
      if (result.created) {
        const response = await listTitles({ ...get().lastFetchParams, limit: 100 })
        set({ titles: response.titles, total: response.total, isLoading: false })
      } else {
        set({ duplicateWarning: result.duplicateWarning, isLoading: false })
      }
      return result
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false })
      return null
    }
  },

  updateTitle: async (id: string, data: UpdateTitleData & { externalIds?: Record<string, string> }) => {
    set({ isLoading: true, error: null })
    try {
      const { externalIds, ...patchData } = data

      // Diff externalIds and make targeted calls
      if (externalIds !== undefined) {
        const current = get().titles.find((t) => t._id === id)
        const currentIds = current?.externalIds ?? {}

        // Set new or changed keys
        for (const [system, value] of Object.entries(externalIds)) {
          if (currentIds[system] !== value) {
            await setTitleExternalId(id, system, value)
          }
        }

        // Remove keys that were deleted in the form
        for (const system of Object.keys(currentIds)) {
          if (!(system in externalIds)) {
            await removeTitleExternalId(id, system)
          }
        }
      }

      await apiUpdate(id, patchData)
      const response = await listTitles({ ...get().lastFetchParams, limit: 100 })
      set({ titles: response.titles, isLoading: false })
      return true
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false })
      return false
    }
  },

  deleteTitle: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      await apiDelete(id)
      set((state) => ({
        titles: state.titles.filter((t) => t._id !== id),
        isLoading: false,
      }))
      return true
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false })
      return false
    }
  },

  clearError: () => set({ error: null }),
  clearDuplicateWarning: () => set({ duplicateWarning: null }),
}))
